/**
 * Gedeelde stukken van de twee audiocaches op de tablet: `SoundCache` (chimes) en `VoiceCache`
 * (spraakclips).
 *
 * Spec: specs/20261020-spraakmeldingen-elevenlabs plan fase 1 ("gemeenschappelijke stukken naar
 * `src/services/audioCacheCore.ts`") · Taak: T091
 *
 * Wat hier staat, is precies wat op de vloot al eens fout ging en daarom op één plek hoort:
 *
 *   • Alleen bytes die zich als `audio/*` aandienen zijn een geluid. De app-host antwoordt op
 *     elk onbekend pad met 200 + `index.html` (de vergiftigde `caro-sounds-v1`, 2026-09-24).
 *   • Cache Storage kan ontbreken (WebView zonder beveiligde context): elke aanraking in een
 *     try, en dan "geen cache".
 *   • Object-URL's die wij maken, geven wij ook weer vrij.
 *
 * ⚠ Geen `AudioContext`, geen `AbortSignal.timeout` (Chromium 69–84).
 */

/** Dient dit `Content-Type` zich aan als audio? */
export function isAudioType(type: string | null | undefined): boolean {
  return !!type && type.toLowerCase().indexOf('audio/') === 0;
}

/** Een geslaagd antwoord met een audio-`Content-Type`. `ok` alleen is niet genoeg. */
export function isAudioResponse(res: Response | null | undefined): boolean {
  return !!res && res.ok && isAudioType(res.headers.get('content-type'));
}

/**
 * Open een benoemde cache, en verwijder één keer per sessie de oude namen. Werpt nooit;
 * `null` = geen Cache Storage.
 */
export function cacheOpener(name: string, staleNames: string[] = []) {
  let staleDropped = false;
  return {
    async open(): Promise<Cache | null> {
      try {
        if (typeof caches === 'undefined') return null;
        if (!staleDropped) {
          staleDropped = true;
          for (const stale of staleNames) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await caches.delete(stale);
            } catch {
              /* een cache die niet weg wil, wordt gewoon niet meer gelezen */
            }
          }
        }
        return await caches.open(name);
      } catch {
        return null;
      }
    },
    reset() {
      staleDropped = false;
    },
  };
}

/** Bijhouden welke `blob:`-URL's van ons zijn, zodat ze weer vrijgegeven kunnen worden. */
export function objectUrlOwner() {
  const owned = new Set<string>();
  return {
    own(url: string | null): string | null {
      if (url && url.indexOf('blob:') === 0) owned.add(url);
      return url;
    },
    release(url: string | null | undefined): void {
      if (!url || !owned.has(url)) return;
      try {
        URL.revokeObjectURL(url);
      } catch {
        /* al vrijgegeven */
      }
      owned.delete(url);
    },
    releaseAll(): void {
      owned.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* al weg */
        }
      });
      owned.clear();
    },
  };
}

/**
 * De bytes van een Blob, ook op een WebView zonder `Blob.prototype.arrayBuffer` (< Chromium 76):
 * dan via `FileReader`, dat er overal is.
 */
export async function bytesOf(blob: Blob): Promise<ArrayBuffer> {
  if (typeof (blob as any).arrayBuffer === 'function') return (blob as any).arrayBuffer();
  if (typeof FileReader !== 'undefined') {
    return new Promise<ArrayBuffer>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error || new Error('FileReader failed'));
      reader.readAsArrayBuffer(blob);
    });
  }
  return new Response(blob).arrayBuffer();
}

/**
 * sha-256 van de bytes als hex, of `null` als `crypto.subtle` er niet is (oude WebView,
 * onbeveiligde context). `null` betekent "niet te controleren", NIET "fout".
 */
export async function sha256Hex(blob: Blob): Promise<string | null> {
  try {
    const subtle = typeof crypto !== 'undefined' && crypto && (crypto as any).subtle;
    if (!subtle || typeof subtle.digest !== 'function') return null;
    // Een kopie als Uint8Array: sommige implementaties weigeren een ArrayBuffer uit een andere
    // realm (FileReader in een WebView-shim, jsdom in de tests).
    const digest: ArrayBuffer = await subtle.digest('SHA-256', new Uint8Array(new Uint8Array(await bytesOf(blob))));
    const bytes = new Uint8Array(digest);
    let hex = '';
    for (let i = 0; i < bytes.length; i += 1) hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
    return hex;
  } catch {
    return null;
  }
}
